import { Injectable, Injector, effect, inject } from '@angular/core';
import { App } from '@capacitor/app';

import { environment } from '../../../environments/environment';
import { AppStatusStore } from '../app-status.store';
import { accessVerdictForCode } from '../app-update';
import { CrudService, type WaitingSend } from '../crud.service';
// Was (2026-10-29): import { GraphqlTransportService, type WaitingSendAnswer } from '../graphql-transport.service';
import { GraphqlTransportService, type WaitingSendAnswer, type WaitingSendFor } from '../graphql-transport.service';
import { WORKS_OFFLINE } from '../needs-connection';
import { RecordDeleteService } from '../record-delete.service';
import { ServerClock } from '../server-clock';
import { OfflineStore } from './offline-store';
import { OfflineWindowService } from './offline-window.service';
import type { UnsavedReason, WaitingChange } from './waiting-changes-store';
import { WaitingChangesService } from './waiting-changes.service';

/** What became of one sent change. */
type Outcome =
  /** Saved: it leaves the list; `updatedAt` is the record's system updated time, for the next change of it. */
  | { kind: 'done'; updatedAt?: string }
  /** Turned down: it stays, as an Unsaved change with this reason, and the run goes on. */
  | { kind: 'refused'; reason: UnsavedReason }
  /** Not judged (Zavo or the network unavailable, or the person not let in): it stays at the head; the run ends. */
  | { kind: 'stop' };

/**
 * Sends the person's Waiting changes once the phone can reach Zavo: one at a time, oldest first, each judged
 * by the server as it arrives. A change that cannot be judged now stops the run and stays first in line; a
 * change Zavo turns down becomes an Unsaved change with its reason and the next one is sent. Only one run is
 * ever going, and every wake while it runs makes it look again once it ends.
 *
 * Each add or edit is built at send time from what the page gave and the entity definitions as they are now,
 * through the same gateway and account check as any save; a delete goes to the delete-record call.
 *
 * Inert unless the app Works offline on a phone, asked again on every run.
 */
@Injectable({ providedIn: 'root' })
export class WaitingChangesSender {
  private readonly waiting = inject(WaitingChangesService);
  private readonly crud = inject(CrudService);
  private readonly transport = inject(GraphqlTransportService);
  private readonly recordDelete = inject(RecordDeleteService);
  private readonly offline = inject(OfflineStore);
  private readonly clock = inject(ServerClock);
  private readonly status = inject(AppStatusStore);
  private readonly offlineWindow = inject(OfflineWindowService);
  private readonly worksOffline = inject(WORKS_OFFLINE);
  private readonly injector = inject(Injector);

  private running: Promise<void> | null = null;
  private again = false;
  private started = false;

  /** Wakes the sender whenever the phone may reach Zavo again: network back, app resumed, a good answer. */
  start(): void {
    // Works offline is asked on every run, not here: a republish read while the app runs can switch it either way.
    if (this.started || !environment.isRuntime) return;
    this.started = true;
    effect(() => { if (this.status.online()) void this.wake(); }, { injector: this.injector });
    effect(() => { if (this.clock.lastConfirmedContact() !== null) void this.wake(); }, { injector: this.injector });
    // The window waits for this run before it unlocks, so a confirmation the run itself did not see still starts one.
    effect(() => { if (this.offlineWindow.awaitsSend()) void this.wake(); }, { injector: this.injector });
    try {
      App.addListener('appStateChange', ({ isActive }) => { if (isActive) void this.wake(); })
        .catch(() => { /* no App plugin: the other wakes still run */ });
    } catch { /* no App plugin */ }
  }

  /** Starts a run, or, when one is going, has it look again once it ends. Resolves when sending stops. */
  wake(): Promise<void> {
    if (this.running) {
      this.again = true;
      return this.running;
    }
    this.running = (async () => {
      try {
        do {
          this.again = false;
          await this.run().catch((e) => console.error('[offline] sending the Waiting changes stopped', e));
        } while (this.again);
      } finally {
        this.running = null;
        // The changes have been sent and judged, so the window may now unlock.
        this.offlineWindow.release();
      }
    })();
    return this.running;
  }

  private async run(): Promise<void> {
    // Switched off, the kept list stays exactly as it is: nothing is sent, and nothing sent could be taken off it.
    const on = () => this.worksOffline() === true;
    if (!on()) return;
    await this.waiting.ready();
    while (on() && this.status.online()) {
      // Taken before the list is read: a wipe while it is read means the change picked from it is gone.
      const generation = this.offline.generation;
      const next = (await this.waiting.list()).find((c) => !c.unsaved && this.waiting.isMine(c));
      if (!next) return;
      if (!(await this.send(next, generation))) return;
    }
  }

  /** Sends one change and keeps what became of it. False when the run must end. */
  private async send(change: WaitingChange, generation: number): Promise<boolean> {
    // Any wipe since the change was picked means the store it came from is gone: it is not sent, and nothing
    // of it is written back.
    const wiped = () => this.offline.generation !== generation;
    // Asked again right before it goes: a sign-in as someone else needs no sign-out, so no wipe marks it, and
    // the request would carry the new person's token.
    const mayGo = () => !wiped() && this.waiting.isMine(change);
    // The send calls check it once more after the token is in hand, which this check cannot see past.
    const owner = ownerOf(change);
    if (wiped()) return false;
    await this.offline.useAs(change.projectId, change.personId);
    if (wiped()) return false;
    const inDoubt = change.outcomeUnknown;
    if (change.op !== 'delete' && !change.outcomeUnknown) {
      // Marked before it goes, so a crash before its answer is kept reads it back instead of trusting the time alone.
      await this.waiting.edit((list) => list.map((c) => (c.changeId === change.changeId ? { ...c, outcomeUnknown: true } : c)));
      if (wiped()) return false;
    }
    let outcome: Outcome;
    if (change.op === 'delete') {
      if (!mayGo()) return false;
      // Was (2026-10-29): const answer = await this.recordDelete.sendWaiting(change.entityId, change.recordId, notChangedSince(change));
      const answer = await this.recordDelete.sendWaiting(change.entityId, change.recordId, owner, notChangedSince(change));
      if (wiped()) return false;
      outcome = judgeDelete(answer);
    } else {
      const built = await this.crud.buildWaitingSend(change);
      if (!built) {
        // The entity is gone, or the change no longer fits its definition.
        outcome = { kind: 'refused', reason: 'not-accepted' };
      } else {
        // Building waits on the entity definitions, which can take a while on a cold start.
        if (!mayGo()) return false;
        // Was (2026-10-29): const answer = await this.transport.sendWaiting(built.body, change.op === 'edit' ? notChangedSince(change) : undefined);
        const answer = await this.transport.sendWaiting(built.body, owner, change.op === 'edit' ? notChangedSince(change) : undefined);
        if (wiped()) return false;
        outcome = await this.judgeSave(change, inDoubt, built, answer);
        if (wiped()) return false;
      }
    }
    if (outcome.kind === 'stop') return false;
    if (outcome.kind === 'refused') {
      const judgement = { reason: outcome.reason, at: Date.now() + (this.clock.offsetMs() ?? 0), seen: false };
      await this.waiting.edit((list) => list.map((c) => (c.changeId === change.changeId ? { ...c, unsaved: judgement } : c)));
      return !wiped();
    }
    const updatedAt = outcome.updatedAt;
    await this.waiting.edit((list) => {
      const at = list.findIndex((c) => c.changeId === change.changeId);
      return list.flatMap((c, i) => {
        if (i === at) return [];
        // Only later changes of the record: an earlier, turned-down one must still be judged by its own time.
        if (updatedAt && i > at && c.entityId === change.entityId && c.recordId === change.recordId) return [{ ...c, knownUpdatedAt: updatedAt }];
        return [c];
      });
    });
    return !wiped();
  }

  private async judgeSave(change: WaitingChange, inDoubt: boolean, built: WaitingSend, answer: WaitingSendAnswer): Promise<Outcome> {
    const early = judgeCommon(answer);
    if (early) return early;
    const { status, code, json } = answer as Extract<WaitingSendAnswer, { status: number }>;
    if (status === 409 || code === 'CHANGED_SINCE') {
      // Sent before and no answer kept: it may be its own earlier landing that the server is pointing at.
      if (change.op === 'edit' && inDoubt) {
        const there = await this.readBack(built, change);
        if (there.kind === 'stop') return there;
        if (there.kind === 'found' && there.matches) return { kind: 'done', updatedAt: there.updatedAt };
      }
      return { kind: 'refused', reason: 'changed-since' };
    }
    if (status === 403) return { kind: 'refused', reason: 'no-access' };
    if (status < 200 || status >= 300) return { kind: 'refused', reason: 'not-accepted' };
    const errors = Array.isArray(json?.errors) && json.errors.length ? json.errors : null;
    if (errors) {
      // A record with this id is already there: saved by an earlier send whose answer was lost, or restored
      // from a backup. Done only if this person can read it back; never overwritten.
      if (change.op === 'add' && errors.some(isPrimaryKeyConflict)) {
        const there = await this.readBack(built, change);
        if (there.kind === 'stop') return there;
        if (there.kind === 'found') return { kind: 'done', updatedAt: there.matches ? there.updatedAt : undefined };
      }
      return { kind: 'refused', reason: 'not-accepted' };
    }
    const row = json?.data?.[built.rootAlias];
    // An update that matched no row: the record is not there any more.
    if (!row) return { kind: 'refused', reason: change.op === 'add' ? 'not-accepted' : 'deleted' };
    return { kind: 'done', updatedAt: typeof row.systemUpdatedAt === 'string' ? row.systemUpdatedAt : undefined };
  }

  /**
   * Reads the record as the server holds it now, as this person. `matches` when every value the change set
   * is already there; only then is its system updated time safe to send later changes with, since no one
   * else's change to those values can be in between.
   */
  private async readBack(built: WaitingSend, change: WaitingChange):
    Promise<{ kind: 'found'; matches: boolean; updatedAt?: string } | { kind: 'missing' } | { kind: 'stop' }> {
    const columns = comparableColumns(built);
    // Was (2026-10-29): const answer = await this.transport.sendWaiting(this.crud.buildByIdRead(built.entity, change.recordId, [...columns, 'systemUpdatedAt']));
    const answer = await this.transport.sendWaiting(this.crud.buildByIdRead(built.entity, change.recordId, [...columns, 'systemUpdatedAt']), ownerOf(change));
    const early = judgeCommon(answer);
    if (early) return { kind: 'stop' };
    const { status, json } = answer as Extract<WaitingSendAnswer, { status: number }>;
    const row = status >= 200 && status < 300 && !json?.errors ? json?.data?.rows?.[0] : null;
    if (!row) return { kind: 'missing' };
    // Values in list rows and linked records are not compared, so a change with no plain value is never taken as there.
    const matches = columns.length > 0 && columns.every((k) => sameValue(row[k], built.rootSet[k]));
    return { kind: 'found', matches, updatedAt: typeof row.systemUpdatedAt === 'string' ? row.systemUpdatedAt : undefined };
  }
}

/**
 * The answers every send shares: no answer, an outage on Zavo's side and an account the server cannot let in
 * all end the run with the change kept first; a Removed answer also ends it, and the Removed path wipes. A
 * change found no longer the signed-in person's was never sent, and stays waiting for its own person.
 */
function judgeCommon(answer: WaitingSendAnswer): Outcome | null {
  // Was (2026-10-29): if (answer.unanswered || answer.withdrawn) return { kind: 'stop' };
  if (answer.unanswered || answer.withdrawn || answer.notTheirs) return { kind: 'stop' };
  const { status, code } = answer;
  const verdict = accessVerdictForCode(code);
  if (verdict === 'unavailable' || status === 503) return { kind: 'stop' };
  if (verdict === 'removed' || verdict === 'not-yet' || status === 401) return { kind: 'stop' };
  // A server error with no code (a 502 "Upstream error") is an outage, not a judgement of the change.
  if (status >= 500 && !code) return { kind: 'stop' };
  return null;
}

/** Whose the change is: every send of it goes with this person's token, to this project, or not at all. */
function ownerOf(change: WaitingChange): WaitingSendFor {
  return { projectId: change.projectId, personId: change.personId };
}

function judgeDelete(answer: WaitingSendAnswer): Outcome {
  const early = judgeCommon(answer);
  if (early) return early;
  const { status, code } = answer as Extract<WaitingSendAnswer, { status: number }>;
  if (status >= 200 && status < 300) return { kind: 'done' };
  // Already gone: deleted by an earlier send whose answer was lost, or by someone else.
  if (status === 404 && code === 'RECORD_NOT_FOUND') return { kind: 'done' };
  if (status === 409 || code === 'CHANGED_SINCE') return { kind: 'refused', reason: 'changed-since' };
  if (status === 403) return { kind: 'refused', reason: 'no-access' };
  return { kind: 'refused', reason: 'not-accepted' };
}

/**
 * The not-changed-since time an edit or delete is sent with: when it was made, or the record's own system
 * updated time from an earlier save of it, whichever is later. The returned time is sent as the server
 * wrote it, microseconds and all.
 */
function notChangedSince(change: WaitingChange): string {
  const known = change.knownUpdatedAt;
  if (known && Date.parse(known) >= change.madeAt) return known;
  return new Date(change.madeAt).toISOString();
}

function isPrimaryKeyConflict(error: any): boolean {
  const text = `${error?.message ?? ''} ${JSON.stringify(error?.extensions ?? {})}`;
  return error?.extensions?.code === 'constraint-violation' && /_pkey/.test(text);
}

/** The record's own values the change set, which a by-id read can show: no links, no list rows, no system values. */
function comparableColumns(built: WaitingSend): string[] {
  const fields = built.entity.data || [];
  return Object.keys(built.rootSet).filter((k) => {
    if (k === 'id' || k.startsWith('system')) return false;
    const field = fields.find((f: any) => f.id === k);
    return !!field && field.type !== 'ENTITY_REFERENCE' && field.type !== 'ARRAY_OF_OBJECT';
  });
}

/** Equal as the server would store them: a number sent as text, or a time written another way, still counts. */
function sameValue(held: unknown, sent: unknown): boolean {
  if (held === sent) return true;
  if ((held === null || held === undefined) && (sent === null || sent === undefined)) return true;
  if (held === null || held === undefined || sent === null || sent === undefined) return false;
  const a = Number(held);
  const b = Number(sent);
  if (typeof held !== 'object' && typeof sent !== 'object' && held !== '' && sent !== '' && Number.isFinite(a) && Number.isFinite(b)) return a === b;
  if (typeof held === 'string' && typeof sent === 'string') {
    const ta = Date.parse(held);
    const tb = Date.parse(sent);
    if (Number.isFinite(ta) && Number.isFinite(tb)) return ta === tb;
  }
  return JSON.stringify(held) === JSON.stringify(sent);
}
